import { useState, useEffect, lazy, Suspense } from 'react';
import AccessDenied from '../../../../../components/AccessDenied';
import DataTable from '../../../../../components/DataTable';
import SpeedDialFAB from '../../../../../components/SpeedDialFAB';

import { PRIMARY_COLOR, DAY_NAMES } from './constants';
import WeekView from './WeekView';
import DayView from './DayView';
import MonthView from './MonthView';
import BookingsSuccessModal from './BookingsSuccessModal';
import { useBookingsTableConfig } from './BookingsTableConfig';
import { useBookingsData, type BookingRow } from './useBookingsData';

// Lazy-loaded heavy children. None render on first paint of the bookings
// list, so they shouldn't sit in the initial bundle.
//
// BookingsModals wraps 7 modal components (Wizard, Form, View, Assign,
// Cancel, Reassign, AvailabilityBlock) - ~3,000 LOC of UI that's invisible
// until the operator clicks an action button.
//
// TeamAvailability is the calendar for the "Team" tab - 785 LOC that's
// invisible until the tab is selected.
//
// Both are pre-fetched on mount (see useEffect below) so by the time the
// operator actually opens a modal or switches tabs, the chunks are warm.
// Suspense fallback is null for modals (they're invisible by default) and
// a small loading line for the team tab.
const BookingsModals = lazy(() => import('./BookingsModals'));
const TeamAvailability = lazy(() => import('../TeamAvailability'));

/**
 * Bookings Page - List and Calendar views
 * Uses view tabs pattern like POS Services
 */
function BookingsList() {
  const data = useBookingsData();

  // Single view mode: list | day | week | month | team
  const [viewMode, setViewMode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    if (viewParam && ['list', 'day', 'week', 'month', 'team'].includes(viewParam)) {
      return viewParam;
    }
    const saved = localStorage.getItem('bookingsViewMode');
    if (saved && ['list', 'day', 'week', 'month', 'team'].includes(saved)) {
      return saved;
    }
    return window.innerWidth < 768 ? 'day' : 'week';
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('bookingsViewMode', viewMode);
  }, [viewMode]);

  // Warm the lazy chunks in the background while the operator scans the
  // list. By the time they click an action button or switch to the Team
  // tab the chunk is already in cache, so no Suspense spinner shows.
  // Best-effort, errors are swallowed.
  useEffect(() => {
    import('./BookingsModals').catch(() => { /* prefetch is best-effort */ });
    import('../TeamAvailability').catch(() => { /* prefetch is best-effort */ });
  }, []);

  // ── Formatting Helpers ────────────────────────────────────────

  const formatBookingDate = (dateStr: string) => {
    if (!dateStr) return '-';
    if (data.formatDate) return data.formatDate(dateStr);
    // Fallback for YYYY-MM-DD local-country text — split, don't parse via Date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // ── Table Config ────────────────────────────────────────────

  const { columns, filters } = useBookingsTableConfig({
    formatBookingDate,
    statusOptions: data.statusOptions,
    operatorOptions: data.operatorOptions,
    territoryOptions: data.territoryOptions,
    contextOptions: data.contextOptions,
    sourceOptions: data.sourceOptions,
  });

  // ── Calendar Navigation ───────────────────────────────────────

  function handlePrevMonth() {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }
  function handleNextMonth() {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }
  function handlePrevWeek() {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  }
  function handleNextWeek() {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  }
  function handlePrevDay() {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }
  function handleNextDay() {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  }
  function handleToday() {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  }

  // ── Permission Check ──────────────────────────────────────────

  if (data.permissionLoading) return null;
  if (!data.canAccess) return <AccessDenied />;

  // ── Shared calendar view props ────────────────────────────────

  const calendarProps = {
    isMobile: data.isMobile,
    calendarFilters: data.calendarFilters,
    setCalendarFilters: data.setCalendarFilters,
    calendarFiltersExpanded: data.calendarFiltersExpanded,
    setCalendarFiltersExpanded: data.setCalendarFiltersExpanded,
    activeCalendarFilterCount: data.activeCalendarFilterCount,
    statusOptions: data.statusOptions,
    territoryOptions: data.territoryOptions,
    operatorOptions: data.operatorOptions,
    filteredCalendarBookings: data.filteredCalendarBookings,
    showBlocks: data.showBlocks,
    operatorBlocks: data.operatorBlocks,
    operatorPatterns: data.operatorPatterns,
    handleViewBooking: data.handleViewBooking as never,
    handlePrevDay,
    handlePrevWeek,
    handlePrevMonth,
    handleNextDay,
    handleNextWeek,
    handleNextMonth,
    handleToday,
    selectedDate,
    currentMonth,
    viewMode,
    DAY_NAMES
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="bookings-page">

      {/* All Modals — lazy, invisible until something triggers a modal */}
      <Suspense fallback={null}>
      <BookingsModals
        viewingBooking={data.viewingBooking}
        setViewingBooking={data.setViewingBooking}
        handleEditBooking={data.handleEditBooking}
        handleCloneBooking={data.handleCloneBooking}
        handleRebook={data.handleRebook}
        canEdit={data.canEdit}
        canAssign={data.canAssign}
        canCancel={data.canCancel}
        canCreate={data.canCreate}
        canReassignTerritory={data.canReassignTerritory}
        operatorCount={data.operatorCount}
        formatCurrency={data.formatCurrency}
        formatDate={data.formatDate}
        timezone={data.timezone}
        assigningBooking={data.assigningBooking}
        setAssigningBooking={data.setAssigningBooking}
        handleSaveAssignment={data.handleSaveAssignment}
        cancellingBooking={data.cancellingBooking}
        setCancellingBooking={data.setCancellingBooking}
        handleSaveCancellation={data.handleSaveCancellation}
        reassigningTerritory={data.reassigningTerritory}
        setReassigningTerritory={data.setReassigningTerritory}
        handleSaveReassignTerritory={data.handleSaveReassignTerritory}
        showBlockModal={data.showBlockModal}
        setShowBlockModal={data.setShowBlockModal}
        handleSaveBlock={data.handleSaveBlock}
        showFormModal={data.showFormModal}
        editingBooking={data.editingBooking}
        rebookCustomer={data.rebookCustomer}
        handleCloseFormModal={data.handleCloseFormModal}
        handleSaveBooking={data.handleSaveBooking}
        handleAssign={data.handleAssign}
        handleCancel={data.handleCancel}
        handleReassignTerritory={data.handleReassignTerritory}
        supabase={data.supabase}
        loadBookings={data.loadBookings}
        setSuccessModal={data.setSuccessModal}
      />
      </Suspense>

      {/* View Toggle Tabs - Single row: List | Day | Week | Month | Team */}
      <div className={`page-tabs ${data.isMobile ? 'page-tabs--mobile' : ''}`}>
        {['list', 'day', 'week', 'month', 'team'].map(mode => (
          <button
            key={mode}
            className={`page-tab ${viewMode === mode ? 'active' : ''} ${data.isMobile ? 'page-tab--mobile' : ''}`}
            onClick={() => setViewMode(mode)}
            style={viewMode === mode ? { color: PRIMARY_COLOR, borderBottomColor: PRIMARY_COLOR } : {}}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {/* Success Modal */}
      <BookingsSuccessModal
        successModal={data.successModal}
        onClose={() => data.setSuccessModal(null)}
        formatBookingDate={formatBookingDate}
      />

      {/* Error state */}
      {data.error && (
        <div className="alert alert-danger mb-lg">
          {data.error}
          <button className="btn btn-sm ml-md" onClick={data.loadBookings}>Retry</button>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <DataTable
          columns={columns}
          data={data.bookings}
          loading={data.loading}
          searchable={true}
          searchPlaceholder="Search reference, customer, suburb..."
          searchKeys={['booking_reference', 'simplybook_code', 'customer_name', 'customer_email', 'suburb']}
          filters={filters}
          defaultSort={{ key: 'created_at', direction: 'desc' }}
          pageSize={50}
          headerActions={{
            primary: data.canCreate ? { label: '+ Book', onClick: data.handleNewBooking, icon: '📅' } : undefined,
            secondary: { label: '+ Block', onClick: data.handleBlockTime, icon: '🚫' },
            onSync: { label: 'Refresh', onClick: data.loadBookings },
            overflow: [
              { label: 'Export CSV', onClick: data.handleExportCSV }
            ]
          }}
          actions={(row) => {
            const b = row as unknown as BookingRow;
            const isPending = b.status === 'pending';
            const isConfirmed = b.status === 'confirmed' || b.status === 'active' || b.status === 'approved';
            const showEdit = data.canEdit && (isPending || isConfirmed);
            const showAssign = data.canAssign && (isPending || isConfirmed);
            const hasAssignment = b.operator_names && b.operator_names.length > 0;
            const showReassign = hasAssignment && data.operatorCount !== null && data.operatorCount >= 1;

            return (
              <>
                <span className="action-icon" onClick={(e) => { e.stopPropagation(); data.handleViewBooking(b); }} title="View">👁️</span>
                {showEdit && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); data.handleEditBooking(b); }} title="Edit">✏️</span>
                )}
                {showAssign && !hasAssignment && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); data.handleAssign(b); }} title="Assign operators">👤</span>
                )}
                {showAssign && showReassign && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); data.handleAssign(b); }} title="Reassign operators">👤</span>
                )}
              </>
            );
          }}
          onRowClick={(row) => data.handleViewBooking(row as unknown as BookingRow)}
          emptyMessage={
            <div className="text-center p-lg">
              <p className="empty-state-message mb-md">No bookings found</p>
              <p className="text-muted text-sm mb-md">
                {data.canCreate
                  ? "Click '+ Book' above to create your first booking, or adjust filters if you expect to see bookings."
                  : "Try adjusting the filters above, or contact your administrator if you need help."}
              </p>
              <a href="mailto:support@cleanmybed.com" className="btn btn-outline btn-sm inline-flex-center">
                {'\uD83D\uDCE7'} Contact Support
              </a>
            </div>
          }
        />
      )}

      {/* Calendar Views - Day/Week/Month */}
      {viewMode === 'day' && <DayView {...calendarProps} />}
      {viewMode === 'week' && <WeekView {...calendarProps} setSelectedDate={setSelectedDate} setViewMode={setViewMode} />}
      {viewMode === 'month' && <MonthView {...calendarProps} setSelectedDate={setSelectedDate} setViewMode={setViewMode} />}

      {/* Team Availability View — lazy chunk, prefetched on mount above */}
      {viewMode === 'team' && (
        <div className="card">
          <div className="card-body">
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading team availability…</div>}>
              <TeamAvailability />
            </Suspense>
          </div>
        </div>
      )}

      {/* On the list tab, DataTable auto-renders the Book/Block FAB from
          headerActions. On the calendar/team tabs there is no DataTable,
          so render a standalone FAB so operators can still book or block
          time without flipping back to list view. Hidden when the user
          can't create — block alone wouldn't be enough to justify it. */}
      {viewMode !== 'list' && data.canCreate && (
        <SpeedDialFAB
          actions={[
            { label: 'Book', icon: '📅', onClick: data.handleNewBooking },
            { label: 'Block', icon: '🚫', onClick: data.handleBlockTime },
          ]}
        />
      )}
    </div>
  );
}

export default BookingsList;
